/**
 * URL redaction for CAPABILITY paths — the one rule, shared by every sink.
 *
 * ── Why this exists (Guest QR Platform, ruling R18) ─────────────────────────
 * The guest table page lives at `/{slug}/t/{token}`, and that token IS the
 * authorization: whoever holds it reads the table's open bill, and from piece 4
 * orders onto it. There is no session, no cookie and no sign-in behind it — the
 * URL is the credential.
 *
 * Analytics SDKs send URLs by default and on every event, not just pageviews:
 * posthog-js stamps `$current_url` / `$pathname` / `$referrer` on each capture,
 * and Sentry attaches `request.url`, the `Referer` header and navigation
 * breadcrumbs to each error. So one un-redacted capture deposits a live table
 * token in a third-party warehouse that a large number of people can query, and
 * it stays there. Redaction has to happen at the LAST step before the wire —
 * not at the call site — because the call sites are the SDKs themselves.
 *
 * ── Why ONE function ────────────────────────────────────────────────────────
 * Two sinks (PostHog, Sentry) and two application points inside PostHog alone.
 * A second copy is how one of them keeps redacting while the other quietly
 * stops — the copied-helper drift class this repo has been bitten by before.
 * It is pure, has NO imports, and is exported from both the package root and
 * the dependency-free `./sentry` subpath, so a `sentry.*.config.ts` loaded at
 * process boot can use it without dragging the analytics graph in.
 *
 * ── Scope: the TABLE path only, deliberately ────────────────────────────────
 * `/f/{token}` (anonymous item feedback) and `/pay/{token}` (public payment
 * links) are also capability URLs, and they are NOT touched here. Each is a
 * different surface with its own ruling and its own anonymity contract, and
 * widening this rule on the way past would change what those two surfaces
 * report without anyone deciding to. Add them when someone decides to — with
 * their own tests.
 */

/** What a redacted token is replaced BY. Exported so tests pin the literal. */
export const REDACTED_CAPABILITY_TOKEN = '[token]';

/**
 * The table-page capability path: `/<slug>/t/<token>`, anchored at the start of
 * the PATH. Exactly three leading segments, the second of which is literally
 * `t` — so a slug that merely contains a `t` (`t-bone-grill`, `table-talk`), a
 * host called `t.example.com`, and the unrelated `/f/<token>` are all untouched,
 * while a restaurant whose slug is literally `t` (`/t/t/<token>`) still works.
 *
 * Everything after the token — a deeper path, `?query`, `#hash` — is preserved:
 * the campaign attribution on a scanned QR (`?source=qr&oq=…`) is the whole
 * reason these events are collected, and only the credential has to go.
 */
const TABLE_CAPABILITY_PATH = /^(\/[^/?#]+\/t\/)[^/?#]+/;

/**
 * Rewrite `/{slug}/t/{token}` to `/{slug}/t/[token]` in a path or absolute URL.
 *
 * Total and non-throwing by contract — it runs inside a `beforeSend`/
 * `before_send`, where an exception would drop the event (or worse, the SDK's
 * whole queue) rather than surface anything. Anything it does not recognise
 * comes back exactly as it went in.
 */
export function redactCapabilityPaths<T>(url: T): T {
  if (typeof url !== 'string' || url.length === 0) return url;

  // Split `scheme://authority` off first. Without this an absolute URL's HOST
  // is the first path-looking segment, so `https://x/t/…` would be matched
  // against the wrong thing.
  const schemeEnd = url.indexOf('://');
  let prefix = '';
  let path: string = url;
  if (schemeEnd !== -1) {
    const authorityEnd = url.indexOf('/', schemeEnd + 3);
    if (authorityEnd === -1) return url; // origin with no path — nothing to redact
    prefix = url.slice(0, authorityEnd);
    path = url.slice(authorityEnd);
  }

  const redacted = path.replace(
    TABLE_CAPABILITY_PATH,
    `$1${REDACTED_CAPABILITY_TOKEN}`,
  );
  return (redacted === path ? url : prefix + redacted) as T;
}

/**
 * A property name that carries a URL.
 *
 * ── Why a SHAPE and not a list (ruling R19) ─────────────────────────────────
 * The first version of this redaction enumerated three posthog property names.
 * posthog-js sends the landing URL under six MORE, and every one is populated
 * exactly when the session starts on the table page — which is the only session
 * with a table token in it:
 *
 *   `$session_entry_url` / `$session_entry_pathname` / `$session_entry_referrer`
 *     — session properties, merged into EVERY event, not just `$pageview`.
 *   `$initial_current_url` / `$initial_pathname` / `$initial_referrer`
 *     — `get_initial_props()`, which lands on `$set_once`.
 *
 * Sentry's tracing side has the same problem with `url.full` and
 * `http.request.header.referer`. A list is a thing an SDK release adds to
 * silently, and the failure is invisible: the events keep flowing and one more
 * field in them is a live credential.
 *
 * The rule is therefore TOKEN-based, and covers both naming conventions in one
 * pass — posthog's `$snake_case` and Sentry's `dotted.otel` keys. `referer`
 * (HTTP's one-r spelling) counts; `$referring_domain` and `server.address` do
 * NOT, because a host carries no path and therefore no token.
 */
const URL_KEY_TOKEN = /^(url|urls|uri|href|pathname|referer|referrer)$/i;

export function isUrlShapedKey(key: string): boolean {
  if (typeof key !== 'string' || key.length === 0) return false;
  return key.split(/[.\-_$[\]]+/).some((token) => URL_KEY_TOKEN.test(token));
}

/**
 * Redact every url-shaped STRING value in a flat property bag, in place.
 *
 * Only strings are touched, and only where {@link isUrlShapedKey} says the key
 * names a URL — a number, an object or a value under any other key comes back
 * identical (by reference, for the non-primitives). Non-table URLs pass through
 * `redactCapabilityPaths` unchanged, so a broad key match costs nothing.
 *
 * Total and non-throwing: it runs inside a `before_send`/`beforeSend`, where an
 * exception drops the event rather than surfacing anything.
 */
export function redactUrlShapedValues(
  record: Record<string, unknown> | null | undefined,
): void {
  if (!record || typeof record !== 'object') return;
  for (const key of Object.keys(record)) {
    const value = record[key];
    if (typeof value === 'string' && isUrlShapedKey(key)) {
      record[key] = redactCapabilityPaths(value);
    }
  }
}
